"use client";

// Login v2 — usa PublicShell + clases globales de globals.css.
//
// Antes este page tenía su propio `<style jsx>` con `.login2-root` y todas
// las clases de form duplicadas. Eso causaba un FOUC en hard-reload porque
// styled-jsx tarda en aplicar mientras el SSR ya pinta el body con el bg
// default de globals (#F9FAFB) — se veía un fondo gris por un frame antes
// de que apareciera el crema. Moviendo todo a globals.css + PublicShell,
// los estilos están disponibles desde el primer paint sin esperar JS.
//
// Lógica preservada:
//   - supabase.auth.signInWithPassword con errores humanizados
//   - Turnstile CAPTCHA opcional (gateado por isTurnstileEnabled)
//   - recordLogin para audit log
//   - Redirect a /superadmin si is_superadmin, sino /[locale]/overview

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { recordLogin, verifyLoginCaptcha } from "./actions";
import { Turnstile } from "@/components/Turnstile";
import { isTurnstileEnabled } from "@/lib/turnstile";
import { BrandMark } from "@/components/brand/BrandMark";
import { PublicShell } from "@/components/brand/PublicShell";

export default function LoginPage({ params }: { params: { locale: string } }) {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileEnabled = isTurnstileEnabled();

  // Mensajes pre-cargados desde ?error=... o ?confirmed=1. Vienen de
  // redirects del layout del dashboard (sesión sin empresa), del callback
  // OAuth, o de /confirm (flow propio de confirmación de email, mig 156 +
  // app/confirm/route.ts). Leemos directo de window.location.search en vez
  // de useSearchParams() para no forzar un Suspense boundary acá.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("error");
    const confirmed = params.get("confirmed");
    if (confirmed === "1") {
      setNotice("Tu email está confirmado. Iniciá sesión con tu email y contraseña.");
      return;
    }
    if (code === "no_company") {
      setError("Tu cuenta no terminó de configurarse. Iniciá sesión de nuevo o registrá una empresa desde cero.");
    } else if (code === "auth_callback_failed") {
      // Caso amplio: lo emite el fallback de /auth/callback cuando ningún
      // branch específico aplicó. Mensaje neutro — antes hablaba de "link
      // de confirmación" pero el user puede no haber clickeado ningún
      // link (ej. URL accedida a mano).
      setError("No pudimos completar el inicio de sesión. Probá de nuevo desde el login.");
    } else if (code === "confirm_invalid") {
      setError("El link de confirmación no es válido. Si ya activaste tu cuenta, iniciá sesión normalmente.");
    } else if (code === "confirm_expired") {
      setError("El link de confirmación expiró. Iniciá sesión normalmente — si tu cuenta ya está activa, vas a entrar.");
    } else if (code === "oauth_signup_disabled") {
      setError("Tu email no está registrado en Talenv. Si todavía no tenés cuenta, registrate desde el link de abajo. Si ya tenés, usá email + contraseña.");
    } else if (code === "oauth_cancelled") {
      // Provider OAuth (Google) devolvió access_denied — user cancelo el flow
      // o denegó permisos. Mensaje específico para evitar la confusión del
      // "link expirado" que veía antes.
      setError("Cancelaste el inicio de sesión con Google. Probá de nuevo o ingresá con tu email y contraseña.");
    } else if (code === "oauth_failed") {
      setError("No pudimos completar el login con Google. Probá de nuevo o usá email + contraseña.");
    }
  }, []);

  // Login con Google — solo para usuarios que YA existen en la base.
  // Si el email no está registrado, Supabase rechaza (porque desactivamos
  // "Allow new users to sign up" en el dashboard) y caemos al manejo del
  // error oauth_signup_disabled de arriba.
  async function handleGoogleSignIn() {
    setError(null);
    setLoading(true);
    const supabase = createClient();
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=/${params.locale}/overview`,
        queryParams: {
          // `access_type=offline` + `prompt=consent` aseguran que pidamos el
          // refresh token la primera vez. Si el user después saca el acceso
          // desde su Google Account, se les vuelve a pedir consentimiento.
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (oauthError) {
      setError("No se pudo iniciar el login con Google. Intentá de nuevo.");
      setLoading(false);
    }
    // Si OK, el browser navega a Google y vuelve por /auth/callback. No
    // tenemos que llamar a router.push acá.
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setError(null);

    if (turnstileEnabled) {
      if (!captchaToken) {
        setError("Resolvé el desafío anti-bot para continuar.");
        setLoading(false);
        return;
      }
      const captchaRes = await verifyLoginCaptcha(captchaToken);
      if (!captchaRes.ok) {
        setError("Verificación anti-bot falló. Recargá la página.");
        setLoading(false);
        return;
      }
    }

    const supabase = createClient();
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      setError(
        authError.message === "Invalid login credentials"
          ? "Email o contraseña incorrectos"
          : authError.message === "Email not confirmed"
          ? "Confirmá tu email antes de iniciar sesión"
          : "Error al iniciar sesión. Intentá de nuevo.",
      );
      setLoading(false);
      return;
    }
    if (data.user) {
      recordLogin(data.user.id).catch(() => {});
      const { data: profile } = await supabase
        .from("users").select("is_superadmin").eq("id", data.user.id).single();
      if ((profile as any)?.is_superadmin === true) {
        router.push("/superadmin");
        router.refresh();
        return;
      }
    }
    router.push(`/${params.locale}/overview`);
    router.refresh();
  }

  return (
    <PublicShell locale={params.locale}>
      <div className="card-header">
        <div className="card-icon">
          <BrandMark size={44} rounded={11} />
          talenv
        </div>
        <h1 className="card-title">Iniciá sesión en tu cuenta</h1>
        <p className="card-sub">Continuá donde lo dejaste.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="field">
          <div className="field-row">
            <label htmlFor="email" className="field-label">Email</label>
          </div>
          <div className="input-wrap">
            <input
              type="email"
              id="email"
              className="input"
              placeholder="tu@empresa.com"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <div className="field-row">
            <label htmlFor="password" className="field-label">Contraseña</label>
            <Link href={`/${params.locale}/forgot-password`}>¿No recordás la contraseña?</Link>
          </div>
          <div className="input-wrap">
            <input
              type={showPwd ? "text" : "password"}
              id="password"
              className="input with-toggle"
              placeholder="••••••••"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              {showPwd ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.79 19.79 0 014.22-5.94M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.5 19.5 0 01-2.16 3.19M14.12 14.12a3 3 0 11-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="form-meta">
          <input
            type="checkbox"
            id="remember"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <label htmlFor="remember">Recordarme en este dispositivo</label>
        </div>

        {turnstileEnabled && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <Turnstile
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              theme="auto"
            />
          </div>
        )}

        {notice && (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              background: "#ECFDF5",
              border: "1px solid #A7F3D0",
              color: "#065F46",
              fontSize: 13,
              lineHeight: 1.45,
              marginBottom: 12,
            }}
          >
            {notice}
          </div>
        )}
        {error && <div className="error-box">{error}</div>}

        <button type="submit" className="btn-submit" disabled={loading}>
          {loading ? "Iniciando…" : "Iniciar sesión"}
        </button>

        {/* Divisor + login con Google. Solo entra si el email ya está
            registrado en la base (la setting "Allow new signups" está
            desactivada en Supabase). Si el email no existe, le redirigimos
            con error `oauth_signup_disabled` desde /auth/callback. */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          margin: "20px 0 14px", color: "#A19FB6", fontSize: 12,
        }}>
          <div style={{ flex: 1, height: 1, background: "rgba(21,16,61,0.10)" }} />
          <span>o</span>
          <div style={{ flex: 1, height: 1, background: "rgba(21,16,61,0.10)" }} />
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
            gap: 10, padding: "11px 16px", borderRadius: 10,
            background: "white",
            border: "1px solid rgba(21,16,61,0.18)",
            color: "#15103D",
            fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#FAF7F2"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
        >
          {/* Logo G oficial de Google */}
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
            <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
          </svg>
          Continuar con Google
        </button>
      </form>

      <div className="card-foot">
        ¿No tenés cuenta?{" "}
        <Link href={`/${params.locale}/demo`}>Pedí una demo</Link>
      </div>
    </PublicShell>
  );
}
